import NoticeItem from "../../../stores/domainStore/pages/models/noticeItem";
import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router";
import {useStores} from "../../../stores/useStores";
import {format} from "date-fns";
import CreateEditForm from "../../molecules/createEditForm/createEditForm";
import {RoutePaths} from "../../../shared/routePaths";
import Banner from "../../atoms/banner/banner";
import {IconSource} from "../../../shared/enums/iconSource";
import {observer} from "mobx-react-lite";
import "./viewNoticeTemplate.sass";
import {NoticeStatus} from "../../../shared/enums/noticeStatus";
import {Navigation} from "../../../utils/navigation";
import {utcToZonedTime} from "date-fns-tz";
import EditField from "../../molecules/editField/editField";
import {NoticeType} from "../../../shared/enums/noticeType";
import ImageSelector from "../../molecules/imageSelector/imageSelector";
import MarkdownToHtmlViewer from "../../atoms/markdownToHtmlViewer/markdownToHtmlViewer";
import Tag from "../../atoms/tag/tag";
import LabelAndValue from "../../molecules/labelAndValue/labelAndValue";
import {BreadcrumbItem} from "../../atoms/breadcrumb/breadcrumb";
import {useVillageFeatureToggle} from "../../../utils/featureToggle/villageFeatureToggle";
import {ServicedApartmentResidentsFeatureName} from "../../../shared/constants";

export interface IViewNoticeTemplateProps {
  notice: NoticeItem;
  isLoading?: boolean;
}

const ViewNoticeTemplate: React.FC<IViewNoticeTemplateProps> = ({notice}) => {
  const {t} = useTranslation(["translation", "notice"]);
  const history = useHistory();

  const {
    domainStore: {noticesDomainStore},
    uiStore: {accountStore}
  } = useStores();

  const formatDate = (dateFormat: string, date?: Date | null): string => {
    if (date) return format(date, dateFormat);
    return "";
  };

  useEffect(() => {
    noticesDomainStore.getVillages();
  }, [noticesDomainStore, noticesDomainStore.editableNotice]);

  const villages = (): string => {
    if (!accountStore.abilities.can("Update", "MultiVillage")) {
      return t("event:labels:multipleVillage");
    }

    const villages = notice.villages.selectedItems.map((value) => value.label);
    return villages.join(", ");
  };

  // Audience is only relevant on the multi-village calendar or in a single
  // village that has the Serviced Apartment residents feature enabled - same
  // condition used to show the audience picker on the create/edit screen.
  const hasServicedApartmentResidents = useVillageFeatureToggle(
    ServicedApartmentResidentsFeatureName
  ).hasFeature.value;
  const showAudienceSelection =
    notice.isMultiVillage || hasServicedApartmentResidents;

  const audience = (): string => {
    const labels: string[] = [];
    if (notice.targetIndependent.value) {
      labels.push(t("notice:labels.targetIndependent"));
    }
    if (notice.targetServiced.value) {
      labels.push(t("notice:labels.targetServiced"));
    }
    return labels.length > 0 ? labels.join(", ") : "-";
  };

  const noticeType = (() => {
    switch (notice.noticeType.text) {
      case NoticeType.General:
        return t("notice:labels.noticeType.general");
      case NoticeType.Important:
        return t("notice:labels.noticeType.important");
      case NoticeType.Announcement:
        return t("notice:labels.noticeType.announcement");
      default:
        return t("notice:labels.noticeType.general");
    }
  })();

  const breadcrumbItem: BreadcrumbItem = {
    name: t("notice:titles.update"),
    onClick: () =>
      Navigation.navigateWithSelectedVillageId(history, RoutePaths.Notices),
    child: {name: notice?.title.text}
  };

  return (
    <>
      <CreateEditForm>
        <CreateEditForm.Header breadcrumbItem={breadcrumbItem} />
        <CreateEditForm.Body>
          <div className="notice-content">
            <div className="notice-title">
              <span data-testid="title">{notice.title.text}</span>
              <span data-testid="notificationDate" className={"date-header"}>
                {formatDate("dd MMMM", notice.notificationDate)}
              </span>
              <span data-testid="noticeStatus">
                {notice.status === NoticeStatus.Published && (
                  <Tag text={t("notice:labels.published")} type="success" />
                )}
              </span>
            </div>

            {notice.isMultiVillage &&
              !accountStore.abilities.can("Create", "MultiVillage") && (
                <Banner
                  text={t("notice:labels.noticePermissionBanner", {
                    createdBy: notice.creatorName ?? "-"
                  })}
                  icon={IconSource.info}
                />
              )}

            <div className="title" data-testid="notice-title">
              <LabelAndValue
                labelText={t("notice:labels.title")}
                valueText={notice.title.text}
              />
            </div>

            {notice.isMultiVillage && (
              <>
                <div className="form-separator" />

                <LabelAndValue
                  labelText={t("event:labels.villages")}
                  valueText={villages()}
                />
              </>
            )}

            {!!notice.image.fileId && (
              <>
                <div>
                  <ImageSelector
                    imageLoading={false}
                    value={notice.image}
                    label={t("notice:labels.heroImage")}
                    onImageSelect={() => {}}
                    disabled={true}
                  />
                </div>
                <div className="image-caption" data-testid="image-caption">
                  <LabelAndValue
                    labelText={t("notice:labels.imageCaption")}
                    valueText={notice.imageCaption.text}
                  />
                </div>
              </>
            )}

            <div className="form-separator" />

            <div className="body" data-testid="body">
              <EditField label={t("notice:labels.body")}>
                <MarkdownToHtmlViewer markdownText={notice.body.text} />
              </EditField>
            </div>

            <div className="form-separator" />

            <div className="author" data-testid="author">
              <LabelAndValue
                labelText={t("notice:labels.author")}
                valueText={notice.author.text}
              />
            </div>

            {showAudienceSelection && (
              <>
                <div className="form-separator" />

                <div className="audience" data-testid="audience">
                  <LabelAndValue
                    labelText={t("notice:labels.audienceTitle")}
                    valueText={audience()}
                  />
                </div>
              </>
            )}

            <div className="form-separator" />

            <div className="notice-type-and-notification">
              <div className="notice-type" data-testid="notice-type">
                <LabelAndValue
                  labelText={t("notice:labels.type")}
                  valueText={noticeType}
                />
              </div>

              <div
                className="send-notification"
                data-testid="send-notification"
              >
                <LabelAndValue
                  labelText={t("notice:titles.sendNotification")}
                  valueText={
                    notice.notificationEnabled.value
                      ? t("translation:confirmations.affirmative")
                      : t("translation:confirmations.negative")
                  }
                />
              </div>
            </div>

            <div className="form-separator" />

            <div className="expiry-date" data-testid="expiry-date">
              <LabelAndValue
                labelText={t("notice:labels.expiryDate")}
                valueText={
                  notice.expiryDate.date
                    ? formatDate(
                        "dd/MM/yyyy hh:mm a",
                        utcToZonedTime(
                          notice.expiryDate.date,
                          noticesDomainStore.getTimeZone()
                        )
                      )
                    : "-"
                }
              />
            </div>
          </div>
        </CreateEditForm.Body>
      </CreateEditForm>
    </>
  );
};

export default observer(ViewNoticeTemplate);
